import moment from 'moment-timezone'
import { MAX_MINUTES, WALMART_SHIPPER_ID } from 'apps/detention-review/constants/detention'
import { stopDate, stopTime, stopTimeZone } from 'utils/appointmentHelpers'
import { FORMATS } from 'constants/datetime'

export const showTCR = (shipperId: string | number) => shipperId === WALMART_SHIPPER_ID

export function findAccessorial<T extends { id?: string }>(
  accessorials: T[] = [],
  id: string
): T | undefined {
  return accessorials.find(a => a.id && parseInt(a.id, 10) === parseInt(id, 10))
}

export const isShipmentFlagEnabled = (
  flagsProp: string[] | undefined,
  flagName: string
): boolean => {
  const flags = flagsProp || []
  return [...flags].includes(flagName)
}

export const calculateRate = (minutes: number, rate: number) => {
  if (minutes >= MAX_MINUTES) {
    return Math.round(MAX_MINUTES / 15) * rate
  }
  return minutes > 0 ? Math.round(minutes / 15) * rate : 0
}

const convertMinsToHrsMins = (minutes: number) => {
  if (Number.isNaN(minutes) || minutes < 0) return '0.0h'

  if (minutes >= MAX_MINUTES) {
    const maxh = Math.floor(MAX_MINUTES / 60)
    const maxm = MAX_MINUTES % 60
    return `${maxh}.${maxm}h`
  }

  const minutesRounded = Math.round(minutes / 15) * 15
  let h: string | number = Math.floor(minutesRounded / 60)
  let m: string | number = minutesRounded % 60
  h = h < 10 ? `${h}` : h
  m = m < 10 ? `${m}` : m
  return `${h}h ${m}m`
}

interface IGetMinuteProps {
  appointment?: { startAtDate?: string | number; startAtTime?: string | number }
  endDateTime: string
  startDateTime: string
}

export const getMinutes = ({
  appointment = {},
  endDateTime = '',
  startDateTime = '',
}: IGetMinuteProps) => {
  const appointmentStart = moment(
    `${appointment.startAtDate} ${appointment.startAtTime}`,
    FORMATS.datetimeInputFormat
  )
  const getIsISO8601 = (date: moment.MomentInput) => moment(date, moment.ISO_8601).isValid()
  const getIs12H = (date: moment.MomentInput) =>
    moment(date, FORMATS.datetime12HInputFormat).isValid()
  const getInputFormat = (date: moment.MomentInput) => {
    if (getIsISO8601(date)) return moment.ISO_8601
    if (getIs12H(date)) return FORMATS.datetime12HInputFormat
    return FORMATS.datetimeInputFormat
  }
  const start = moment(startDateTime, getInputFormat(startDateTime))
  const end = moment(endDateTime, getInputFormat(endDateTime))
  const startTimeCalc = start.isBefore(appointmentStart) ? appointmentStart : start
  // First 2 hours are free
  const minutes =
    startDateTime && endDateTime ? moment.duration(end.diff(startTimeCalc)).asMinutes() - 120 : 0
  return minutes
}

export const getEquipmentName = (parentId: number | undefined) =>
  parentId === 2 ? 'Reefer' : 'Dry van'

export const getEligibleTime = (minutes: number) => convertMinsToHrsMins(minutes)

export const getCalculatedRate = ({
  hourlyRate,
  minutes,
}: {
  minutes: number
  hourlyRate: number
}) => calculateRate(minutes, hourlyRate / 4)

export const formatDetentionTime = (timeDate: moment.MomentInput, timezone: string) => {
  const date = timeDate && timezone ? moment(timeDate).tz(timezone).format(FORMATS.dateFormat) : ''
  const time =
    timeDate && timezone ? moment(timeDate).tz(timezone).format(FORMATS.militaryTimeFormat) : ''
  return { date, time }
}

interface IStop {
  appointment?: {
    startAt?: string
    endAt?: string
    timezone?: string
    startAtTime?: string | null
  }
  facility?: { shipperFacilityUuid?: string }
}

export const selectedFacilityAppointmentTime = (
  stops: IStop[],
  detention: { shipperFacilityUuid: string }
) => {
  const selectedFacilityStop = stops.find(
    stop => stop?.facility?.shipperFacilityUuid === detention.shipperFacilityUuid
  )
  const { endAt, startAt, startAtTime, timezone } = selectedFacilityStop?.appointment || {}
  const appointmentTime = stopTime(startAt, endAt, timezone, false, startAtTime)
  const appointmentDate = stopDate(startAt, timezone, FORMATS.dateFormat)
  const appointmentTimezone = stopTimeZone(startAt, endAt, timezone)
  const appointmentDateTime = startAt
    ? `${appointmentDate} ${appointmentTime} ${appointmentTimezone}`
    : ''
  return appointmentDateTime
}
